import React, { useContext } from 'react';
import { FlexHeader } from '../../../shared';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
  blockBenchmarkConfig,
  blockDateRange,
  blockPrivateAssetIsCumulative,
  blockPrivateDefaultAsOfDateBehavior,
  blockSettings,
} from 'venn-state';
import { SmallToggle, usePrivateAnalysis, UserContext } from 'venn-components';
import { Icon, Radio, ShimmerBlock, Tooltip } from 'venn-ui-kit';
import {
  Dates,
  hasDisabledDateRange,
  isPrivatesBlock,
  isPrivatesPerformanceBlock,
  isProjectionBasedPrivatesBlock,
  isPublicPrivateAssetGrowthBlock,
  PRIVATE_NAV_BLOCK_SETTINGS,
  withSuspense,
} from 'venn-utils';
import styled from 'styled-components';
import { PanelSection } from '../../../PanelSection';

/** Section for editing a block config with regard to its {@link blockDateRangeInputState}. */
const PrivateAssetBlockAssignmentSectionContent = ({ selectedBlockId }: { selectedBlockId: string }) => {
  const { hasPermission } = useContext(UserContext);
  const readonlyDates = !hasPermission('STUDIO_EDIT_DATES') || !hasPermission('STUDIO_EDIT');
  const readonlyMetrics = !hasPermission('STUDIO_EDIT_METRICS');
  const benchmarkSubject = useRecoilValue(blockBenchmarkConfig(selectedBlockId));
  const blockSetting = useRecoilValue(blockSettings(selectedBlockId));
  const [defaultAsOfDateBehavior, setDefaultAsOfDateBehavior] = useRecoilState(
    blockPrivateDefaultAsOfDateBehavior(selectedBlockId),
  );
  const [privateAssetIsCumulative, setPrivateAssetIsCumulative] = useRecoilState(
    blockPrivateAssetIsCumulative(selectedBlockId),
  );
  const dateRange = useRecoilValue(blockDateRange(selectedBlockId));

  const customRange = dateRange?.range.to ? Dates.toDDMMMYYYY(dateRange.range.to, 'QUARTERLY') : '--';

  const isPrivate = isPrivatesBlock(blockSetting.customBlockType);
  const isAssetGrowthPrivate = isPublicPrivateAssetGrowthBlock(blockSetting.customBlockType);
  const isProjectionBasedPrivate = isProjectionBasedPrivatesBlock(blockSetting.customBlockType);
  const isPerformanceBasedPrivate = isPrivatesPerformanceBlock(blockSetting.customBlockType);
  const displayCustomAsOfDateSelection = isPerformanceBasedPrivate || isProjectionBasedPrivate || isAssetGrowthPrivate;
  const canShowProFormaDates = !hasDisabledDateRange(blockSetting.customBlockType);
  const benchmark = benchmarkSubject.subject?.name ?? 'None';

  const label = blockSetting.title;
  const disableCumulative = blockSetting.id === PRIVATE_NAV_BLOCK_SETTINGS.id;

  return (
    (isPrivate || isAssetGrowthPrivate) &&
    canShowProFormaDates &&
    displayCustomAsOfDateSelection && (
      <PanelSection header="Input Settings">
        <InputSection>
          <div className="text-base" data-testid="qa-block-date-range">
            <FlexHeader style={{ marginBottom: '0px', fontSize: '12px' }}>
              {isProjectionBasedPrivate && 'Projection Start'}
              {isPerformanceBasedPrivate && 'End Date'}
              {isAssetGrowthPrivate && 'Simulation Start Date'}
            </FlexHeader>
            <div className="flex justify-between items-center width-full">
              <StyledRadio
                inputId={isProjectionBasedPrivate ? 'lastAvailableTransaction' : 'defaultAsOfDateBehavior'}
                value={isProjectionBasedPrivate ? 'lastAvailableTransaction' : 'defaultAsOfDateBehavior'}
                checked={defaultAsOfDateBehavior}
                onChange={() => setDefaultAsOfDateBehavior(!defaultAsOfDateBehavior)}
                disabled={readonlyDates}
              >
                <span className="text-base">
                  {isProjectionBasedPrivate && 'Project from last available transaction'}
                  {isPerformanceBasedPrivate && 'Latest available'}
                  {isAssetGrowthPrivate && 'Current quarter end date'}
                </span>
              </StyledRadio>
              {defaultAsOfDateBehavior &&
                (isPrivate ? (
                  <LastProjectedDate selectedBlockId={selectedBlockId} />
                ) : (
                  <span className="text-venn-grey-80">--</span>
                ))}
            </div>
            <div className="flex justify-between items-center width-full">
              <StyledRadio
                inputId="dateRangeSelect"
                value="dateRangeSelect"
                checked={!defaultAsOfDateBehavior}
                onChange={() => setDefaultAsOfDateBehavior(!defaultAsOfDateBehavior)}
                disabled={readonlyDates}
              >
                <span className="text-base">
                  Date range selected in inputs{' '}
                  <Tooltip
                    usePortal
                    content={
                      <div>
                        {isProjectionBasedPrivate
                          ? 'The end date of the Date Range assigned to this block in the Inputs panel to the left will be used as the projection start date.'
                          : 'The end date of the Date Range assigned to this block in the Inputs panel to the left will be used as the end date here.'}
                      </div>
                    }
                    maxWidth={300}
                  >
                    <Icon prefix="far" type="circle-info" />
                  </Tooltip>
                </span>
              </StyledRadio>
              <span className="text-venn-grey-80">{customRange}</span>
            </div>
          </div>
        </InputSection>
        {isPerformanceBasedPrivate && (
          <InputSection>
            <FlexHeader style={{ marginBottom: '0px', fontSize: '12px' }}>
              Benchmark for Public Market Equivalent
            </FlexHeader>
            <div className="text-base text-venn-grey-80" data-testid="qa-block-private-pme-benchmark">
              {benchmark}
            </div>
          </InputSection>
        )}
        {blockSetting.customBlockType === 'PRIVATE_CASH_FLOW' && label && (
          <div className="text-base font-bold">
            Cumulative {label}
            <SmallToggle
              disabled={readonlyMetrics || disableCumulative}
              toggled={disableCumulative ? false : !!privateAssetIsCumulative}
              onToggle={() => setPrivateAssetIsCumulative(!privateAssetIsCumulative)}
            />
          </div>
        )}
      </PanelSection>
    )
  );
};

const LastProjectedDate = ({ selectedBlockId }: { selectedBlockId: string }) => {
  const { data } = usePrivateAnalysis(selectedBlockId);
  const lastTransactionTo = data?.cashFlows?.[0]?.projectionAsOfDate;
  const text = lastTransactionTo ? Dates.toDDMMMYYYY(lastTransactionTo, 'QUARTERLY') : '--';
  return <span className="text-venn-grey-80">{text}</span>;
};

export const PrivateAssetBlockAssignmentSection = withSuspense(
  <ShimmerBlock />,
  PrivateAssetBlockAssignmentSectionContent,
);

const StyledRadio = styled(Radio)`
  margin-top: 0px;
  margin-bottom: 0px;
  font-size: 12px;
  line-height: 0px;
`;

const InputSection = styled.div`
  display: flex;
  flex-direction: column;
  line-height: normal;
`;
